// ============================================================
// MULTI-TENANT: src/hooks/useTenantTheme.js
// Aplica CSS custom properties no :root baseadas no tenant.
// Chamado uma vez no topo do componente principal (App.js).
// ============================================================

import { useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';

/**
 * Derivada de uma cor HEX, gera uma versão mais clara (para hover, etc.)
 * Simples ajuste de luminosidade sem dependência extra.
 *
 * @param {string} hex - Cor base (#rrggbb)
 * @param {number} amount - Valor a adicionar a cada canal (0–255)
 * @returns {string} Nova cor HEX
 */
function lightenHex(hex, amount = 30) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0x00ff) + amount);
  const b = Math.min(255, (num & 0x0000ff) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Hook que aplica as variáveis CSS do tenant no documento.
 * Também atualiza o título da página com o nome da empresa.
 *
 * Variáveis definidas em :root:
 *   --tenant-cor-primaria       → cor principal (ex: botões, links)
 *   --tenant-cor-primaria-hover → versão mais clara para hover
 *   --tenant-nome               → nome da empresa (CSS não usa, mas fica disponível)
 *
 * @example
 *   // No App.js, dentro do componente principal:
 *   useTenantTheme();
 */
export function useTenantTheme() {
  const { tenant } = useTenant();

  useEffect(() => {
    if (!tenant) return;

    const root = document.documentElement;
    const cor = tenant.cor_primaria || '#16a34a';
    const corHover = lightenHex(cor, 25);

    // MULTI-TENANT: aplica variáveis CSS do tenant
    root.style.setProperty('--tenant-cor-primaria', cor);
    root.style.setProperty('--tenant-cor-primaria-hover', corHover);

    // Atualiza o título da aba do navegador
    if (tenant.nome) {
      document.title = tenant.nome;
    }

    // Cleanup: remove as variáveis ao desmontar (ex: troca de tenant em dev)
    return () => {
      root.style.removeProperty('--tenant-cor-primaria');
      root.style.removeProperty('--tenant-cor-primaria-hover');
    };
  }, [tenant]);
}

export default useTenantTheme;
